import { ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Dialog, cx, focusRing } from '@/components/ui';
import GameLogin from './GameLogin';

export interface PlayLoginDialogProps {
  open: boolean;
  onClose: () => void;
  /** The game's name (shown under the title). */
  gameName?: string;
  username?: string | null;
  password?: string | null;
  /** "Open game": opens the site outside the app. */
  onOpenGame: () => void;
  /** Set when the browser blocked the new window: the link the player can tap instead. */
  blockedUrl?: string | null;
}

/**
 * Play for a game whose site cannot run inside the app (spec 2026-10-08-kiosk-web-split §4): the
 * player's login for the game (copy buttons) and "Open game". When the browser blocks the new
 * window, the game's link is offered as a button to tap (a real link: browsers let a tapped link
 * open a tab). It stays open after the game opens, so the password is still at hand.
 */
export default function PlayLoginDialog({
  open,
  onClose,
  gameName,
  username,
  password,
  onOpenGame,
  blockedUrl,
}: PlayLoginDialogProps) {
  const { t } = useTranslation();
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('play.loginTitle')}
      description={gameName}
      size="md"
      closeLabel={t('common.close')}
      footer={
        <Button variant="primary" size="lg" icon={<ExternalLink />} onClick={onOpenGame}>
          {t('play.openGame')}
        </Button>
      }
    >
      <div className="flex min-w-0 flex-col gap-3">
        <p className="m-0 font-ui text-small text-secondary">{t('play.loginHint')}</p>
        <GameLogin username={username} password={password} />
        {blockedUrl ? (
          <Alert
            tone="warning"
            title={t('play.blockedTitle')}
            action={
              <a
                href={blockedUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="play-blocked-link"
                className={cx(
                  'inline-flex h-13 max-w-full items-center justify-center gap-2 rounded-control border border-edge-strong bg-inset px-5.5 font-ui text-body font-semibold text-link',
                  focusRing
                )}
              >
                <ExternalLink className="size-4 shrink-0" aria-hidden />
                <span className="truncate">{t('play.tapToOpen')}</span>
              </a>
            }
          >
            {t('play.blockedBody')}
          </Alert>
        ) : null}
      </div>
    </Dialog>
  );
}
